import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner, Table } from "react-bootstrap";
import styled from "styled-components";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";
import { colors, constants } from "utils";

const DATASET_PREFIX = constants.TAGGER_DATASET_PREFIX;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
  background: ${colors.white};
  border: 1px solid ${colors.milkGlass400};
  border-radius: 8px;
`;

const ControlField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 240px;
  flex: 1;
`;

const ItemCard = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.milkGlass400};
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  word-break: break-word;
`;

const ItemHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemIndex = styled.span`
  font-weight: 600;
  color: ${colors.blue600 || "#4f46e5"};
`;

const ItemId = styled.span`
  font-family: "SF Mono", Menlo, monospace;
  font-size: 12px;
  color: ${colors.milkGlass600 || "#6b7280"};
`;

const MetaLine = styled.div`
  font-size: 14px;
`;

const ContextDetails = styled.details`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 6px;
  background: ${colors.milkGlass100 || "#fafafa"};
  & > summary {
    cursor: pointer;
    padding: 8px 12px;
    font-size: 13px;
    color: ${colors.milkGlass600 || "#6b7280"};
    user-select: none;
  }
`;

const ContextBody = styled.div`
  padding: 10px 12px;
  font-size: 13px;
`;

const CtxLine = styled.div`
  padding: 2px 0;
  & > .meta {
    color: ${colors.milkGlass600 || "#6b7280"};
    font-family: "SF Mono", Menlo, monospace;
    font-size: 11px;
    margin-right: 6px;
  }
`;

const TagChip = styled.span`
  display: inline-block;
  background: #eef2ff;
  color: #3730a3;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  margin: 2px 4px 2px 0;
  font-family: "SF Mono", Menlo, monospace;
`;

const NoTag = styled.span`
  color: ${colors.milkGlass600 || "#6b7280"};
  font-size: 13px;
`;

const QuoteMeta = styled.div`
  color: ${colors.milkGlass600 || "#6b7280"};
  font-family: "SF Mono", Menlo, monospace;
  font-size: 11px;
  margin-bottom: 2px;
`;

const renderTags = (tags) => {
  if (!tags || tags.length === 0) return <NoTag>—</NoTag>;
  return tags.map((t, i) => <TagChip key={`${t}-${i}`}>{t}</TagChip>);
};

const formatLatency = (ms) => {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms.toFixed(0)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

const shortRunLabel = (runName) => {
  if (!runName) return "";
  const beforeComma = runName.split(",")[0].trim();
  return beforeComma.length > 40 ? `${beforeComma.slice(0, 40)}…` : beforeComma;
};

const RUN_A_COLOR = "#0ea5e9";
const RUN_B_COLOR = "#d97706";

const SummaryRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const SummaryCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;
`;

const SummaryTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${(p) => p.color || "#1f2330"};
`;

const SummaryStat = styled.div`
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  & > .label { color: #6b7280; }
  & > .val { font-family: "SF Mono", Menlo, monospace; }
`;

const renderSummary = (label, color, summary) => {
  if (!summary) return null;
  return (
    <SummaryCol>
      <SummaryTitle color={color}>{label}</SummaryTitle>
      <SummaryStat><span className="label">Items</span><span className="val">{summary.item_count ?? "—"}</span></SummaryStat>
      <SummaryStat><span className="label">Avg latency</span><span className="val">{formatLatency(summary.avg_latency_ms)}</span></SummaryStat>
      <SummaryStat><span className="label">Min / Max</span><span className="val">{formatLatency(summary.min_latency_ms)} / {formatLatency(summary.max_latency_ms)}</span></SummaryStat>
      <SummaryStat><span className="label">Total</span><span className="val">{formatLatency(summary.total_latency_ms)}</span></SummaryStat>
    </SummaryCol>
  );
};

export const TaggerExperimentsComparison = () => {
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("");
  const [runs, setRuns] = useState([]);
  const [runA, setRunA] = useState("");
  const [runB, setRunB] = useState("");

  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [error, setError] = useState(null);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    setLoadingDatasets(true);
    setError(null);
    request("admin/experiments/datasets", { method: "GET" })
      .then((res) => {
        const all = res?.datasets || [];
        setDatasets(all.filter((d) => (d?.name || "").startsWith(DATASET_PREFIX)));
      })
      .catch((e) => {
        logError("Load Langfuse datasets failed", e, { category: LogCategory.ERROR });
        setError(e?.detail || e?.message || "Failed to load datasets");
      })
      .finally(() => setLoadingDatasets(false));
  }, []);

  useEffect(() => {
    if (!selectedDataset) {
      setRuns([]);
      setRunA("");
      setRunB("");
      return;
    }
    setLoadingRuns(true);
    setError(null);
    setRunA("");
    setRunB("");
    request(`admin/experiments/datasets/${encodeURIComponent(selectedDataset)}/runs`, {
      method: "GET",
    })
      .then((res) => {
        setRuns(res?.runs || []);
      })
      .catch((e) => {
        logError("Load Langfuse dataset runs failed", e, { category: LogCategory.ERROR });
        setError(e?.detail || e?.message || "Failed to load runs");
      })
      .finally(() => setLoadingRuns(false));
  }, [selectedDataset]);

  const handleCompare = async () => {
    if (!selectedDataset || !runA || !runB) return;
    if (runA === runB) {
      setError("Pick two different runs.");
      return;
    }
    setLoadingComparison(true);
    setError(null);
    setComparison(null);
    try {
      const res = await request("admin/experiments/compare/tagger", {
        method: "POST",
        payload: { dataset_name: selectedDataset, run_a: runA, run_b: runB },
      });
      setComparison(res);
    } catch (e) {
      logError("Compare experiments failed", e, { category: LogCategory.ERROR });
      setError(e?.detail || e?.message || "Failed to compare experiments");
    } finally {
      setLoadingComparison(false);
    }
  };

  const runOptions = useMemo(
    () =>
      runs.map((r) => (
        <option key={r.id || r.name} value={r.name}>
          {r.name}
        </option>
      )),
    [runs]
  );

  return (
    <Page>
      <h2 style={{ margin: 0 }}>Experiments Comparison</h2>
      <div style={{ color: colors.milkGlass600 || "#6b7280", fontSize: 14 }}>
        Compare two Langfuse dataset runs side-by-side.
      </div>

      <Controls>
        <ControlField>
          <Form.Label style={{ fontWeight: 600 }}>
            Dataset <span style={{ marginLeft: 6, fontSize: 10, color: "#6b7280" }}>▼</span>
          </Form.Label>
          <Form.Control
            as="select"
            value={selectedDataset}
            onChange={(e) => setSelectedDataset(e.target.value)}
            disabled={loadingDatasets}
          >
            <option value="">
              {loadingDatasets ? "Loading datasets…" : "Select a dataset"}
            </option>
            {datasets.map((d) => (
              <option key={d.id} value={d.name}>
                {d.name}
              </option>
            ))}
          </Form.Control>
        </ControlField>

        <ControlField>
          <Form.Label style={{ fontWeight: 600, color: RUN_A_COLOR }}>
            Run A <span style={{ marginLeft: 6, fontSize: 10 }}>▼</span>
          </Form.Label>
          <Form.Control
            as="select"
            value={runA}
            onChange={(e) => setRunA(e.target.value)}
            disabled={!selectedDataset || loadingRuns}
          >
            <option value="">
              {loadingRuns ? "Loading runs…" : "Select run A"}
            </option>
            {runOptions}
          </Form.Control>
        </ControlField>

        <ControlField>
          <Form.Label style={{ fontWeight: 600, color: RUN_B_COLOR }}>
            Run B <span style={{ marginLeft: 6, fontSize: 10 }}>▼</span>
          </Form.Label>
          <Form.Control
            as="select"
            value={runB}
            onChange={(e) => setRunB(e.target.value)}
            disabled={!selectedDataset || loadingRuns}
          >
            <option value="">
              {loadingRuns ? "Loading runs…" : "Select run B"}
            </option>
            {runOptions}
          </Form.Control>
        </ControlField>

        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <Button
            onClick={handleCompare}
            disabled={
              !selectedDataset ||
              !runA ||
              !runB ||
              loadingComparison ||
              loadingRuns
            }
          >
            {loadingComparison ? (
              <>
                <Spinner size="sm" animation="border" /> Comparing…
              </>
            ) : (
              "Compare"
            )}
          </Button>
        </div>
      </Controls>

      {error && <Alert variant="danger">{error}</Alert>}

      {comparison && (
        <>
          {(comparison.run_a_summary || comparison.run_b_summary) && (
            <SummaryRow>
              {renderSummary(shortRunLabel(comparison.run_a), RUN_A_COLOR, comparison.run_a_summary)}
              {renderSummary(shortRunLabel(comparison.run_b), RUN_B_COLOR, comparison.run_b_summary)}
            </SummaryRow>
          )}

          <div style={{ fontSize: 14, color: colors.milkGlass600 || "#6b7280" }}>
            {comparison.items.length} item(s) compared
          </div>

          {comparison.items.map((item, idx) => (
            <ItemCard key={item.dataset_item_id}>
              <ItemHeader>
                <div>
                  <ItemIndex>#{idx + 1}</ItemIndex>{" "}
                  <ItemId>{item.dataset_item_id}</ItemId>
                </div>
                {item.question_prompt && (
                  <MetaLine>
                    <strong>Question:</strong> {item.question_prompt}
                  </MetaLine>
                )}
                {item.question_solution && (
                  <MetaLine style={{ color: "#059669" }}>
                    <strong>Correct answer:</strong> {item.question_solution}
                  </MetaLine>
                )}
                <MetaLine style={{ fontSize: 12, color: "#6b7280" }}>
                  <span style={{ color: RUN_A_COLOR, fontWeight: 600 }}>{shortRunLabel(comparison.run_a)}</span>{" "}
                  {formatLatency(item.run_a_latency_ms)}
                  {"  ·  "}
                  <span style={{ color: RUN_B_COLOR, fontWeight: 600 }}>{shortRunLabel(comparison.run_b)}</span>{" "}
                  {formatLatency(item.run_b_latency_ms)}
                </MetaLine>
              </ItemHeader>

              <ContextDetails>
                <summary>
                  Previous conversation context ({item.previous_context.length} lines)
                </summary>
                <ContextBody>
                  {item.previous_context.length === 0 ? (
                    <NoTag>No previous context</NoTag>
                  ) : (
                    item.previous_context.map((q, i) => (
                      <CtxLine key={i}>
                        <span className="meta">
                          [{q.timestamp || ""}] Student {q.student_id || ""}
                        </span>
                        {q.text || ""}
                      </CtxLine>
                    ))
                  )}
                </ContextBody>
              </ContextDetails>

              <Table bordered hover size="sm" style={{ margin: 0, tableLayout: "fixed", width: "100%", wordBreak: "break-word" }}>
                <thead style={{ background: colors.milkGlass100 || "#f9fafb" }}>
                  <tr>
                    <th style={{ width: "40%" }}>Text</th>
                    <th style={{ width: "20%" }}>Expected output</th>
                    <th style={{ width: "20%", color: RUN_A_COLOR }}>{shortRunLabel(comparison.run_a) || "Run A"}</th>
                    <th style={{ width: "20%", color: RUN_B_COLOR }}>{shortRunLabel(comparison.run_b) || "Run B"}</th>
                  </tr>
                </thead>
                <tbody>
                  {item.quotes.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <NoTag>No transcript quotes in this item.</NoTag>
                      </td>
                    </tr>
                  ) : (
                    item.quotes.map((q) => (
                      <tr key={q.event_id}>
                        <td>
                          <QuoteMeta>
                            [{q.timestamp || ""}] Student {q.student_id || ""}
                          </QuoteMeta>
                          <div>{q.text}</div>
                        </td>
                        <td>{renderTags(q.expected_tags)}</td>
                        <td>{renderTags(q.run_a_tags)}</td>
                        <td>{renderTags(q.run_b_tags)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </ItemCard>
          ))}
        </>
      )}
    </Page>
  );
};
